import { useEffect, useState , useCallback} from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  BadgeCheck, Bookmark, Grid3x3, Users, Store, Share2, Edit3,
  Sparkles, ArrowLeft,
} from "lucide-react";
import { resolveAssetUrl } from "@/lib/assetUrl";
import BusinessLogo from "@/components/BusinessLogo";
import { toast } from "sonner";

/**
 * CuratorProfilePage — public "one-link" profile for a Blinterest curator.
 *
 * URL: `/curator/{handle}` (with or without leading `@` — we normalise).
 *
 * Shows: header (avatar + handle + display name + bio + follower count),
 * stat pills (Finds Kept / Collections / Followers / Following /
 * Businesses Supported), public Collections grid, Recent Finds strip,
 * Businesses I Support strip. If the visitor owns the profile, shows
 * an inline Edit button that opens the claim/bio dialog.
 */
export default function CuratorProfilePage() {
  const { handle: rawHandle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ handle: "", display_name: "", bio: "" });
  const [saving, setSaving] = useState(false);
  const [myHandle, setMyHandle] = useState(null);

  // Fetch caller's own handle so we can detect ownership. Backend enforces
  // this properly on PATCH regardless — this just decides whether to show
  // the Edit button + claim banner.
  useEffect(() => {
    if (!user) { setMyHandle(null); return; }
    axios
      .get(`${API}/user/handle`, { withCredentials: true })
      .then((r) => setMyHandle(r.data.handle))
      .catch(() => setMyHandle(null));
  }, [user]);

  // Normalise `/curator/@Lena` → `/curator/lena` (single source of truth
  // for shares — otherwise the OG cache gets duplicated).
  useEffect(() => {
    if (!rawHandle) return;
    const normalised = rawHandle.replace(/^@/, "").toLowerCase();
    if (normalised !== rawHandle) {
      navigate(`/curator/${normalised}`, { replace: true });
    }
  }, [rawHandle, navigate]);

  const cleanHandle = (rawHandle || "").replace(/^@/, "").toLowerCase();

    const fetchProfile = useCallback(async () => {
    if (!cleanHandle) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/curator/${cleanHandle}`);
      setProfile(r.data);
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [cleanHandle]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const isOwner = Boolean(user && myHandle && profile?.handle
                                 && myHandle === profile.handle);

  const openEdit = () => {
    setForm({
      handle: profile.handle_is_placeholder ? "" : (profile.handle || ""),
      display_name: profile.display_name || "",
      bio: profile.bio || "",
    });
    setEditOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Handle first (may 400/409). Only persist if changed.
      const requestedHandle = form.handle.trim().replace(/^@/, "").toLowerCase();
      let newHandle = null;
      if (requestedHandle && requestedHandle !== profile.handle) {
        const r = await axios.patch(
          `${API}/user/handle`,
          { handle: requestedHandle },
          { withCredentials: true },
        );
        newHandle = r.data.handle;
        toast.success(`Your Blinterest link is now theblkcommunity.com${r.data.profile_url}`);
        // Keep ownership detection intact — otherwise Edit button vanishes
        // until a full page reload.
        setMyHandle(newHandle);
        // The route change below re-triggers fetchProfile via useEffect;
        // don't refetch by the old handle (would 404).
        navigate(r.data.profile_url, { replace: true });
      }
      // Bio + display name second
      if (form.display_name !== (profile.display_name || "")
          || form.bio !== (profile.bio || "")) {
        await axios.patch(
          `${API}/user/profile`,
          {
            display_name: form.display_name,
            bio: form.bio,
          },
          { withCredentials: true },
        );
      }
      setEditOpen(false);
      // Only refetch inline if the handle didn't change — otherwise the
      // navigate() above already scheduled a refetch via useEffect.
      if (!newHandle) fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save profile");
    } finally {
      setSaving(false);
    }
  };

  const shareLink = () => {
    if (typeof window === "undefined" || !profile) return;
    const url = `${window.location.origin}/curator/${profile.handle}`;
    if (navigator.share) {
      navigator.share({
        title: `${profile.display_name || profile.handle} on Blinterest`,
        text: profile.bio || "Discover this curator's Collections on Blinterest.",
        url,
      }).catch(() => {});
      return;
    }
    navigator.clipboard.writeText(url).then(
      () => toast.success("Profile link copied"),
      () => toast.error("Couldn't copy"),
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-tbc-navy">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-pulse">
          <div className="h-32 bg-white/10 rounded-2xl mb-8" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1,2,3,4].map((i) => (
              <div key={i} className="aspect-square bg-white/10 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-tbc-navy">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-16 text-center text-white">
          <h1 className="font-heading text-3xl mb-2">Curator not found</h1>
          <p className="text-white/60 text-sm mb-6">
            That handle isn&apos;t claimed yet. Maybe it&apos;s yours to grab?
          </p>
          <Link to="/blinterest">
            <Button className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full">
              Explore Blinterest
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const s = profile.stats || {};

  return (
    <div className="min-h-screen bg-tbc-navy" data-testid="curator-profile-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <Link
          to="/blinterest"
          className="inline-flex items-center text-white/70 hover:text-white text-sm mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Blinterest
        </Link>

        {/* HEADER */}
        <section className="rounded-2xl bg-white/5 border border-white/10 p-5 sm:p-6 flex flex-col sm:flex-row items-start gap-5">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-tbc-gold/20 flex-none ring-2 ring-tbc-gold/40">
            {profile.picture ? (
              <img
                src={profile.picture}
                alt={profile.display_name || profile.handle}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-tbc-gold font-heading text-2xl">
                {(profile.display_name || profile.handle || "?").charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-2xl sm:text-3xl text-white">
                {profile.display_name || `@${profile.handle}`}
              </h1>
              {!profile.handle_is_placeholder && (
                <BadgeCheck className="w-5 h-5 text-tbc-gold" aria-label="Claimed handle" />
              )}
            </div>
            <p className="text-tbc-gold text-sm font-body">@{profile.handle}</p>
            {profile.bio ? (
              <p className="text-white/75 font-body text-sm mt-2 leading-relaxed max-w-lg">
                {profile.bio}
              </p>
            ) : (
              isOwner && (
                <p className="text-white/40 font-body text-xs italic mt-2">
                  Add a bio so people know what you love to curate.
                </p>
              )
            )}
          </div>
          <div className="flex flex-col sm:items-end gap-2 flex-none">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={shareLink}
                className="border-white/25 text-white bg-transparent hover:bg-white/10 rounded-full h-9"
                data-testid="curator-share-btn"
              >
                <Share2 className="w-4 h-4 mr-1" />
                Share
              </Button>
              {isOwner && (
                <Dialog open={editOpen} onOpenChange={setEditOpen}>
                  <DialogTrigger asChild>
                    <Button
                      onClick={openEdit}
                      className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full h-9"
                      data-testid="curator-edit-btn"
                    >
                      <Edit3 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </DialogTrigger>
                  <EditDialogContent
                    form={form}
                    setForm={setForm}
                    onSave={save}
                    saving={saving}
                    isPlaceholder={profile.handle_is_placeholder}
                  />
                </Dialog>
              )}
            </div>
          </div>
        </section>

        {/* Placeholder-handle banner for owners who haven't claimed a name */}
        {isOwner && profile.handle_is_placeholder && (
          <div
            className="mt-4 rounded-2xl border-2 border-dashed border-tbc-gold/40 bg-tbc-gold/5 p-4 flex items-center justify-between gap-3"
            data-testid="curator-claim-banner"
          >
            <div>
              <p className="text-tbc-gold text-[10px] uppercase tracking-widest font-semibold">
                Claim your name
              </p>
              <p className="text-white/80 text-sm mt-0.5">
                Your Blinterest link is <code className="text-tbc-gold">/curator/{profile.handle}</code>.
                Pick something you love — great for IG bios and TikTok links.
              </p>
            </div>
            <Button
              onClick={openEdit}
              className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full whitespace-nowrap"
              data-testid="curator-claim-btn"
            >
              Claim Handle
            </Button>
          </div>
        )}

        {/* STATS */}
        <section className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-2">
          <StatPill icon={<Bookmark className="w-4 h-4" />} label="Finds Kept" value={s.finds_kept} />
          <StatPill icon={<Grid3x3 className="w-4 h-4" />} label="Collections" value={s.collections} />
          <StatPill icon={<Users className="w-4 h-4" />} label="Followers" value={s.followers} />
          <StatPill icon={<Sparkles className="w-4 h-4" />} label="Following" value={s.following} />
          <StatPill icon={<Store className="w-4 h-4" />} label="Businesses" value={s.businesses_supported} />
        </section>

        {/* COLLECTIONS */}
        {profile.collections?.length > 0 && (
          <section className="mt-8">
            <h2 className="font-heading text-xl text-white mb-3">Collections</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {profile.collections.map((c) => (
                <Link
                  key={c.board_id}
                  to={`/blinterest/board/${c.board_id}`}
                  className="block rounded-2xl overflow-hidden bg-white/5 border border-white/5 hover:border-tbc-gold/40 transition-colors"
                  data-testid={`curator-collection-${c.board_id}`}
                >
                  <div className="aspect-square overflow-hidden bg-white/5">
                    {c.cover_image ? (
                      <img
                        src={resolveAssetUrl(c.cover_image)}
                        alt={c.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-tbc-gold/20 to-tbc-navy/60" />
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="font-body text-sm text-white truncate">{c.name}</p>
                    <p className="text-white/50 text-xs">{c.pin_count || 0} Finds</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* RECENT FINDS */}
        {profile.recent_finds?.length > 0 && (
          <section className="mt-10">
            <h2 className="font-heading text-xl text-white mb-3">Recent Finds</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {profile.recent_finds.map((p) => (
                <Link
                  key={p.pin_id}
                  to={`/blinterest/pin/${p.pin_id}`}
                  className="block rounded-2xl overflow-hidden bg-white/5 border border-white/5 hover:border-tbc-gold/40 transition-colors"
                  data-testid={`curator-find-${p.pin_id}`}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={resolveAssetUrl(p.image_url)}
                      alt={p.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <p className="text-white/80 text-xs px-2 py-1.5 truncate">{p.title}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* BUSINESSES SUPPORTED */}
        {profile.businesses_supported?.length > 0 && (
          <section className="mt-10 mb-6">
            <h2 className="font-heading text-xl text-white mb-3">Businesses I Support</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {profile.businesses_supported.map((b) => (
                <Link
                  key={b.business_id}
                  to={`/business/${b.business_id}`}
                  className="flex flex-col items-center text-center gap-2 rounded-2xl bg-white/5 border border-white/5 hover:border-tbc-gold/40 p-3 transition-colors"
                  data-testid={`curator-support-${b.business_id}`}
                >
                  <BusinessLogo name={b.name} logoUrl={b.logo_url} size="lg" shape="rounded" />
                  <p className="font-body text-white text-xs line-clamp-1 flex items-center gap-1">
                    <span className="truncate">{b.name}</span>
                    {b.is_verified && <BadgeCheck className="w-3 h-3 text-tbc-gold flex-none" />}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {(!profile.collections?.length && !profile.recent_finds?.length) && (
          <section className="mt-10 text-center text-white/60 py-16">
            <p className="font-heading text-white text-xl mb-1">
              No public Finds yet
            </p>
            <p className="text-sm">
              {isOwner
                ? "Start keeping Finds and make one Collection public — this page will fill up automatically."
                : "This curator hasn't shared anything yet. Follow them to be first when they do."}
            </p>
            <Link to="/blinterest" className="inline-block mt-4">
              <Button className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full">
                Explore Blinterest
              </Button>
            </Link>
          </section>
        )}
      </div>
      <Footer />
    </div>
  );
}

// -------- helpers --------------------------------------------------------

function StatPill({ icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/5 p-2.5 text-center">
      <div className="text-tbc-gold flex items-center justify-center mb-1">{icon}</div>
      <p className="text-white text-lg font-heading leading-none">{value ?? 0}</p>
      <p className="text-white/50 text-[10px] uppercase tracking-widest mt-1">{label}</p>
    </div>
  );
}

/**
 * EditDialogContent — the claim/bio form modal.
 * Handle field is prefixed with `@` visually but stored without.
 */
function EditDialogContent({ form, setForm, onSave, saving, isPlaceholder }) {
  return (
    <DialogContent className="sm:max-w-md bg-tbc-navy text-white border-white/10">
      <DialogHeader>
        <DialogTitle className="font-heading text-tbc-gold">
          {isPlaceholder ? "Claim your Blinterest link" : "Edit profile"}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-white/60">Handle</label>
          <div className="mt-1 flex items-center gap-1">
            <span className="text-white/50">@</span>
            <Input
              value={form.handle}
              onChange={(e) => setForm((f) => ({ ...f, handle: e.target.value.toLowerCase() }))}
              placeholder="yourname"
              className="bg-white/10 border-white/20 text-white h-10"
              data-testid="curator-form-handle"
              maxLength={30}
            />
          </div>
          <p className="text-white/50 text-[11px] mt-1">
            2-30 characters. Letters, numbers, hyphens.
          </p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/60">Display name</label>
          <Input
            value={form.display_name}
            onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
            placeholder="Lena · The Home Editor"
            className="bg-white/10 border-white/20 text-white h-10"
            data-testid="curator-form-name"
            maxLength={60}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/60">Bio</label>
          <Textarea
            rows={3}
            value={form.bio}
            onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
            placeholder="What do you love to curate?"
            className="bg-white/10 border-white/20 text-white"
            data-testid="curator-form-bio"
            maxLength={280}
          />
          <p className="text-white/50 text-[11px] mt-1 text-right">
            {form.bio.length}/280
          </p>
        </div>
        <Button
          onClick={onSave}
          disabled={saving}
          className="w-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full h-11"
          data-testid="curator-form-save"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </DialogContent>
  );
}
